/**
 * Authentication Context
 * Handles user authentication state and operations
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as authApi from '../services/authApi';

interface Organization {
  id: string;
  name: string;
  display_name: string;
  theme: string;
  logo_url?: string;
}

interface UserPermissions {
  can_upload_documents: boolean;
  can_extract: boolean;
  can_view_extractions: boolean;
  can_delete_documents: boolean;
  can_manage_users: boolean;
  can_manage_teams: boolean;
  can_view_billing: boolean;
  can_export_data: boolean;
  can_manage_global_rules: boolean;
  can_manage_local_rules: boolean;
}

interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string | null;
  is_admin: boolean;
  is_superadmin: boolean;
  roles: Array<{
    id: string;
    name: string;
    display_name: string;
  }>;
  permissions?: UserPermissions;
  organizations?: Organization[];
  current_organization_id?: string | null;
  current_organization?: Organization | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string, organizationId?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<{ success: boolean; message: string; organization: authApi.Organization }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      setToken(storedToken);
      loadUser(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const loadUser = async (authToken: string) => {
    setIsLoading(true);
    let lastError: unknown;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const userData = await authApi.getCurrentUser(authToken);
        setUser(userData);
        setIsLoading(false);
        return;
      } catch (error: any) {
        lastError = error;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('auth_token');
          setToken(null);
          setUser(null);
          setIsLoading(false);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    console.error('Failed to load user:', lastError);
    setIsLoading(false);
  };

  const login = async (usernameOrEmail: string, password: string, organizationId?: string) => {
    try {
      const response = await authApi.login(usernameOrEmail, password);
      const authToken = response.token;
      
      // Store token first
      localStorage.setItem('auth_token', authToken);
      setToken(authToken);
      setUser(response.user);
      
      // If organizationId provided and user is superadmin, switch to that org
      if (organizationId && response.user.is_superadmin) {
        try {
          await authApi.switchOrganization(authToken, organizationId);
          // Reload user to get updated organization
          const updatedUser = await authApi.getCurrentUser(authToken);
          setUser(updatedUser);
        } catch (err) {
          console.error('Failed to switch organization:', err);
          // Continue with login even if org switch fails
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const switchOrganization = async (organizationId: string) => {
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    try {
      const result = await authApi.switchOrganization(token, organizationId);
      // Reload user to get updated organization
      await refreshUser();
      return result;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to switch organization');
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authApi.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    if (token) {
      await loadUser(token);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    refreshUser,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



